// T-053 pilot: the two sign-in steps on the design system. Same endpoints and messages as the old
// loginBasedTemporaryCode.js / loginTemporary.js (jQuery + sweetalert), without either.
// T-101: step 1 sends the captcha token with the form (the server checks it after repeated failures) and the server
// answers with a signed challenge in an HttpOnly cookie; step 2 sends only the code. No mobile number in any address.
import type { ToastOptions } from '@soren/ui/scripts/ds/toast.ts';
import { digitsOnly } from '@soren/ui/scripts/rules.ts';

/** What the two sign-in endpoints answer (each field only when it applies). */
interface SignInAnswer {
  captchaRequired?: boolean;
  wrongUsernameOrPassword?: boolean;
  next?: string;
  challengeExpired?: boolean;
  lockedOut?: boolean;
  wrongTemporaryCode?: boolean;
}
/** The first step's form: its fields by name. */
type RequestForm = HTMLFormElement & { nationalCode: HTMLInputElement; mobileNumber: HTMLInputElement };

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ds.min.js may not have loaded
const toast = (text: string, opt: ToastOptions): unknown => window.ds?.toast(text, opt);
const unbusy = (btn: HTMLButtonElement | null): void => {
  if (!btn) return;
  btn.disabled = false;
  btn.removeAttribute('aria-busy');
  if (btn.dataset.label) btn.innerHTML = btn.dataset.label;
};
const remember = (btn: HTMLButtonElement | null): void => {
  if (btn && !btn.dataset.label) btn.dataset.label = btn.innerHTML;
};
const busy = (btn: HTMLButtonElement | null): void => {
  if (!btn) return;
  btn.disabled = true;
  btn.setAttribute('aria-busy', 'true');
  btn.innerHTML = `<span class="ds-spin" aria-hidden="true"></span>${btn.dataset.busyText || ''}`;
};

// A JSON answer, or a clear error: 429 = too many tries (rate limit), any other failure status = the request failed.
// `page` = a successful non-JSON answer is fine (sign-in success is a redirect to the next page) and reads as {}.
class Refused extends Error {}
async function answer(r: Response, page = false): Promise<SignInAnswer> {
  if (r.status === 429) throw new Refused('تلاش‌ها بیش از حد مجاز شد؛ چند دقیقه بعد دوباره امتحان کنید.');
  if (!r.ok) throw new Error(String(r.status));
  if (r.headers.get('content-type')?.includes('json')) return r.json() as Promise<SignInAnswer>;
  if (page) return {};
  throw new Error('not json');
}
const failed = (err: unknown): unknown =>
  toast(err instanceof Refused ? err.message : 'اتصال به سامانه برقرار نشد؛ دوباره امتحان کنید.', { type: 'danger' });

// step 1: national code + mobile (+ captcha token) → code issued, challenge cookie set → step 2
const request = document.getElementById('ds-otp-request') as RequestForm | null;
if (request) {
  const btn = request.querySelector<HTMLButtonElement>('[type=submit]');
  remember(btn);
  request.addEventListener('submit', async e => {
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ds.min.js may not have loaded
    if (window.ds && !window.ds.validateForm(request)) {
      setTimeout(() => unbusy(btn));
      return;
    }
    const nationalCode = digitsOnly(request.nationalCode.value),
      mobileNumber = mobileOf(request.mobileNumber.value);
    busy(btn);
    try {
      const form = new FormData();
      form.append('nationalCode', nationalCode);
      form.append('mobileNumber', mobileNumber);
      form.append(
        'arcaptcha-token',
        (document.getElementById('arcaptcha-token') as HTMLInputElement | null)?.value ?? '',
      );
      const data = await answer(
        await fetch('/Accounts/LoginBasedTemporaryCode', { method: 'POST', body: form, credentials: 'same-origin' }),
      );
      const captchaNote = document.getElementById('captcha-container');
      if (captchaNote) captchaNote.hidden = data.captchaRequired !== true;
      if (data.captchaRequired === true) {
        resetCaptcha();
        unbusy(btn);
        return;
      }
      if (data.wrongUsernameOrPassword === true) {
        resetCaptcha();
        toast('کدملی با شماره موبایل تطابق ندارد', { type: 'danger', title: 'ورود ممکن نشد' });
        unbusy(btn);
        return;
      }
      // assigning a string to window.location navigates (the old scripts' way; same as location.href = …)
      window.location = (data.next || '/Accounts/LoginWithTemporaryCode') as string & Location;
    } catch (err) {
      failed(err);
      unbusy(btn);
    }
  });
}
// a captcha answer is good for one check: after a refused try the widget asks again
function resetCaptcha(): void {
  try {
    window.arcaptcha?.reset?.();
  } catch {
    /* no widget on the page */
  }
}
function mobileOf(v: string): string {
  let d = digitsOnly(v);
  if (d.startsWith('0098')) d = '0' + d.slice(4);
  else if (d.startsWith('98') && d.length === 12) d = '0' + d.slice(2);
  else if (d.startsWith('9') && d.length === 10) d = '0' + d;
  return d;
}

// step 2: the one-time code
const verify = document.getElementById('ds-otp-verify');
if (verify) {
  const btn = verify.querySelector<HTMLButtonElement>('[type=submit]');
  remember(btn);
  const code = document.getElementById('temporaryCode') as HTMLInputElement;
  let sending = false;
  async function send(): Promise<void> {
    if (sending) return;
    const value = digitsOnly(code.value);
    if (!value) {
      toast('رمز یکبار مصرف باید مقدار داشته باشد', { type: 'danger' });
      unbusy(btn);
      return;
    }
    sending = true;
    busy(btn);
    try {
      const r = await fetch('/Accounts/LoginWithTemporaryCode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temporaryCode: value }),
        credentials: 'same-origin',
      });
      const data = await answer(r, true);
      if (data.challengeExpired === true) {
        toast('مهلت ورود تمام شده است؛ دوباره کد ملی و شماره موبایل را وارد کنید.', { type: 'danger' });
        setTimeout(() => {
          window.location = '/Accounts/LoginBasedTemporaryCode' as string & Location;
        }, 2500);
        return;
      }
      if (data.lockedOut === true) {
        toast('به‌خاطر تلاش‌های ناموفق، ورود این حساب موقتاً قفل شده است؛ بعداً دوباره امتحان کنید.', {
          type: 'danger',
          title: 'ورود موقتاً ممکن نیست',
        });
        unbusy(btn);
        sending = false;
        return;
      }
      if (data.wrongTemporaryCode === true) {
        toast('رمز یکبار مصرف اشتباه است', { type: 'danger' });
        unbusy(btn);
        sending = false;
        return;
      }
      window.location = '/Dashboard/Index' as string & Location;
    } catch (err) {
      failed(err);
      unbusy(btn);
      sending = false;
    }
  }
  // send() shows its own errors; nothing waits for it
  /* eslint-disable @typescript-eslint/no-floating-promises */
  verify.addEventListener('submit', e => {
    e.preventDefault();
    send();
  });
  // all digits entered (typed, pasted or filled from the SMS): sign in at once
  code.addEventListener('input', () => {
    if (digitsOnly(code.value).length === +(verify.querySelector<HTMLElement>('.ds-otp')?.dataset.length || 6)) {
      send();
    }
  });
  /* eslint-enable @typescript-eslint/no-floating-promises */
  // resend after the interval: reopening the page sends a new code
  const timer = document.getElementById('ds-resend-timer'),
    resend = document.getElementById('ds-resend');
  let left = +(timer?.dataset.seconds || 60);
  const tick = (): void => {
    if (left > 0) {
      timer!.textContent = `امکان درخواست مجدد کد تا ${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
      left--;
      setTimeout(tick, 1000);
    } else {
      timer!.textContent = 'کد را دریافت نکرده‌اید؟';
      resend!.hidden = false;
    }
  };
  if (timer) tick();
  // focus the first empty box, but never take focus back from someone already typing (it sent the rest of a fast-typed
  // or auto-filled code into the first box — found by the UI interaction check, 2026-10-06)
  setTimeout(() => {
    const otp = verify.querySelector('.ds-otp');
    if (!otp || otp.contains(document.activeElement)) return;
    [...otp.querySelectorAll('input')].find(c => !c.value)?.focus();
  }, 50);
}
